
"use client";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ExamResultMetadata } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns"; // Standard date formatting if needed, though ISO strings work
import { CheckCircle, XCircle, ArrowRight, Eye } from "lucide-react";

interface ExamHistoryListProps {
    history: ExamResultMetadata[];
    onSelectExam?: (id: string) => void;
}

export default function ExamHistoryList({ history, onSelectExam }: ExamHistoryListProps) {
    return (
        <Card className="col-span-1 lg:col-span-3">
            <CardHeader>
                <CardTitle className="text-lg">Recent Exams</CardTitle>
                <CardDescription>History of your practice sessions</CardDescription>
            </CardHeader>
            <CardContent>
                {history.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                        No exams taken yet. Start practicing!
                    </div>
                ) : (
                    <div className="space-y-4">
                        {history.map((exam) => (
                            <div key={exam.id} className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors">
                                <div className="flex items-center space-x-4">
                                    <div className="">
                                        {exam.passed ?
                                            <CheckCircle className="w-5 h-5 text-green-500" /> :
                                            <XCircle className="w-5 h-5 text-red-500" />
                                        }
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-sm">
                                            {new Date(exam.timestamp).toLocaleDateString(undefined, {
                                                weekday: 'short',
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </h4>
                                        <div className="flex items-center gap-2 mt-1">
                                            <Badge variant="outline" className="text-xs font-normal">
                                                {exam.mode === 'exam' ? 'Exam Mode' : 'Full Mode'}
                                            </Badge>
                                            <span className="text-xs text-muted-foreground">
                                                Max Score: {exam.maxScore}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-4">
                                    <div className="text-right">
                                        <span className={`block text-lg font-bold ${exam.passed ? 'text-green-600' : 'text-red-500'}`}>
                                            {Math.round(exam.percentage)}%
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                            {exam.score} / {exam.maxScore} pts
                                        </span>
                                    </div>
                                    <div className="text-right hidden sm:block min-w-[80px]">
                                        {exam.timeTaken !== undefined && (
                                            <span className="text-xs font-mono text-muted-foreground block">
                                                {Math.floor(exam.timeTaken / 60)}m {Math.abs(exam.timeTaken % 60)}s
                                            </span>
                                        )}
                                    </div>
                                    <Button variant="ghost" size="icon" onClick={() => onSelectExam && onSelectExam(exam.id)} title="View Details">
                                        <Eye className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
